export const TOOLBOX_SERVICE_UUID = '12345678-1234-1234-1234-123456789012';
export const TOOLBOX_STATUS_SERVICE_UUID = '12345678-1234-1234-1234-123456789013';
export const TOOLBOX_POWER_STATUS_CHAR_UUID = '12345678-1234-1234-1234-123456789011';
export const TOOLBOX_TEMPERATURE_STATUS_CHAR_UUID = '12345678-1234-1234-1234-123456789012';
export const TOOLBOX_SETTINGS_STATUS_CHAR_UUID = '12345678-1234-1234-1234-123456789014';
export const TOOLBOX_COMMAND_CHAR_UUID = '12345678-1234-1234-1234-123456789019';

export const COMMAND_UNLOCK_PASSPHRASE = 'Pike||Pine!';

export type PowerStatus = {
  voltageBattery: number;
  current: number;
  powerWatts: number;
  fuelGauge: number;
  dutyPercent: number;
};

export type TemperatureStatus = {
  temperaturePCB: number;
  temperatureESP32: number;
  temperatureBattery: number;
};

export type SettingsStatus = {
  centerFrequency: number;
  currentFrequency: number;
  buttonPhysicallyPressed: boolean;
  buttonEnabled: boolean;
  autotuneActive: boolean;
  sweepActive: boolean;
  sleepEnabled: boolean;
  qiCharging: boolean;
  healthCheckCriticalStop: boolean;
  warningState: boolean;
  lowBatteryState: boolean;
  overheatState: boolean;
  autotuneInitialStepSize: number;
  autotuneMaxBandwidth: number;
  autotuneHumEnabled: boolean;
  autotuneSeekMaximum: boolean;
  odometerTotalMinutes: number;
  odometerActiveTimeBufferMs: number;
  autotuneHumFrequencyStatus: number;
  autotuneHumVolumeStatus: number;
  gestureDetectionEnabled: boolean;
  gestureRotationThreshold: number;
  multiFreqModeStatus: boolean;
  multiFreqDwellStatus: number;
  autoTuneMode: string;
};

export type SweepPoint = {
  frequencyHz: number;
  powerW: number;
};

export type CommandStatusSnapshot = {
  currentFrequency?: number;
  current?: number;
  powerWatts?: number;
  voltageBattery?: number;
  temperaturePCB?: number;
  temperatureBattery?: number;
  temperatureESP32?: number;
};

const SWEEP_POINT_REGEX = /^f(\d+):p([+-]?(?:\d+\.?\d*|\.\d+))$/;

export function parseSweepPointLine(line: string): SweepPoint | null {
  const match = line.trim().match(SWEEP_POINT_REGEX);
  if (!match) {
    return null;
  }
  const frequencyHz = Number(match[1]);
  const powerW = Number(match[2]);
  if (!Number.isFinite(frequencyHz) || !Number.isFinite(powerW)) {
    return null;
  }
  return { frequencyHz, powerW };
}

export function isSweepEndLine(line: string): boolean {
  return line.trim() === 'sweepEnd';
}

export function parsePowerStatus(payload: string): PowerStatus | null {
  const parts = payload
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length < 5) {
    return null;
  }

  const values = parts.slice(0, 5).map((part) => Number(part));
  if (values.some((value) => !Number.isFinite(value))) {
    return null;
  }

  return {
    voltageBattery: values[0],
    current: values[1],
    powerWatts: values[2],
    fuelGauge: values[3],
    dutyPercent: values[4],
  };
}

export function parseTemperatureStatus(payload: string): TemperatureStatus | null {
  const parts = payload
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (parts.length < 3) {
    return null;
  }

  const values = parts.slice(0, 3).map((part) => Number(part));
  if (values.some((value) => !Number.isFinite(value))) {
    return null;
  }

  return {
    temperaturePCB: values[0],
    temperatureESP32: values[1],
    temperatureBattery: values[2],
  };
}

export function parseSettingsStatus(payload: string): SettingsStatus | null {
  const values = payload.split(',');
  if (values.length < 25) {
    return null;
  }

  const numbers = values.slice(0, 24).map((entry) => Number(entry));
  if (numbers.some((value) => !Number.isFinite(value))) {
    return null;
  }

  return {
    centerFrequency: numbers[0],
    currentFrequency: numbers[1],
    buttonPhysicallyPressed: Boolean(numbers[2]),
    buttonEnabled: Boolean(numbers[3]),
    autotuneActive: Boolean(numbers[4]),
    sweepActive: Boolean(numbers[5]),
    sleepEnabled: Boolean(numbers[6]),
    qiCharging: Boolean(numbers[7]),
    healthCheckCriticalStop: Boolean(numbers[8]),
    warningState: Boolean(numbers[9]),
    lowBatteryState: Boolean(numbers[10]),
    overheatState: Boolean(numbers[11]),
    autotuneInitialStepSize: numbers[12],
    autotuneMaxBandwidth: numbers[13],
    autotuneHumEnabled: Boolean(numbers[14]),
    autotuneSeekMaximum: Boolean(numbers[15]),
    odometerTotalMinutes: numbers[16],
    odometerActiveTimeBufferMs: numbers[17],
    autotuneHumFrequencyStatus: numbers[18],
    autotuneHumVolumeStatus: numbers[19],
    gestureDetectionEnabled: Boolean(numbers[20]),
    gestureRotationThreshold: numbers[21],
    multiFreqModeStatus: Boolean(numbers[22]),
    multiFreqDwellStatus: numbers[23],
    autoTuneMode: values[24] || 'SINGLE_FREQ',
  };
}

export function parseFactoryInfoLines(lines: string[]): Record<string, string> {
  const result: Record<string, string> = {};
  lines.forEach((line) => {
    const separatorIndex = line.indexOf(':');
    if (separatorIndex < 1) {
      return;
    }
    const key = line.slice(0, separatorIndex).trim();
    const value = line.slice(separatorIndex + 1).trim();
    if (!key || !value) {
      return;
    }
    result[key] = value;
  });
  return result;
}

export function parseGetStatusResponse(response: string): CommandStatusSnapshot | null {
  const snapshot: CommandStatusSnapshot = {};

  const frequency = readLabeledNumber(response, 'Frequency');
  if (frequency != null) {
    snapshot.currentFrequency = frequency;
  }

  const current = readLabeledNumber(response, 'Current');
  if (current != null) {
    snapshot.current = current;
  }

  const power = readLabeledNumber(response, 'Power');
  if (power != null) {
    snapshot.powerWatts = power;
  }

  const batteryVoltage = readLabeledNumber(response, 'BatteryVoltage');
  if (batteryVoltage != null) {
    snapshot.voltageBattery = batteryVoltage;
  }

  const pcbTemp = readLabeledNumber(response, 'PCBTemp');
  if (pcbTemp != null) {
    snapshot.temperaturePCB = pcbTemp;
  }

  const batteryTemp = readLabeledNumber(response, 'BatteryTemp');
  if (batteryTemp != null) {
    snapshot.temperatureBattery = batteryTemp;
  }

  const esp32Temp = readLabeledNumber(response, 'ESP32Temp');
  if (esp32Temp != null) {
    snapshot.temperatureESP32 = esp32Temp;
  }

  if (Object.keys(snapshot).length === 0) {
    return null;
  }
  return snapshot;
}

function readLabeledNumber(text: string, label: string): number | null {
  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`${escapedLabel}\\s*:\\s*([+-]?(?:\\d+\\.?\\d*|\\.\\d+))`, 'i');
  const match = text.match(regex);
  if (!match) {
    return null;
  }
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}
